import { useBILogger } from '@wix/fe-essentials/react';
import { useCallback } from 'react';
import { buttonClickSrc164Evid324 } from '@wix/bi-logger-editor-platform-data/v2';
import { TargetComponents, StylesDerivedNativeActionNames } from '../../types';
import { getTargetComponentsIDList } from '../../common/grouping/helpers/getTargetComponentsIDList';
import {
  BACK_BUTTON_NAME,
  CONTROL_TYPE_PLACEHOLDER,
  CONTROL_ID_PLACEHOLDER,
  PANEL_NAME_BY_ACTION,
  PRESET_CONTROL_TYPE,
  PRESET_CONTROL_ID_BY_ACTION,
  CONTAINER_NAME_TYPE,
  LOBBY_ITEM_BUTTON_NAME_BY_ACTION,
  CONTAINER_NAME_LOBBY_BY_ACTION,
} from './constants';
import {
  getBaseComponentChangeLogParams,
  getBaseParams,
} from './getParamsUtils';
import { getTargetComponentsFormattedDisplayNames } from '../../common/grouping/helpers/getTargetComponentsFormattedDisplayNames';

type ValueChangePayload = {
  prevValues: string;
  newValues: string;
  targetComponents?: TargetComponents;
  controlType?: string;
  controlId?: string;
};

export const useBI = () => {
  const logger = useBILogger();

  const trackLobbyItemClick = useCallback(
    (actionName: StylesDerivedNativeActionNames) => {
      const buttonName = LOBBY_ITEM_BUTTON_NAME_BY_ACTION[actionName];
      const containerName =
        CONTAINER_NAME_LOBBY_BY_ACTION[CONTAINER_NAME_TYPE.LOBBY][actionName];

      if (!buttonName || !containerName) {
        return;
      }

      logger.report(
        buttonClickSrc164Evid324({
          ...getBaseParams(),
          button_name: buttonName,
          container_name: containerName,
        }),
      );
    },
    [logger],
  );

  const trackBackButtonClick = useCallback(
    (actionName: StylesDerivedNativeActionNames) => {
      const containerName =
        CONTAINER_NAME_LOBBY_BY_ACTION[CONTAINER_NAME_TYPE.PANEL][actionName];

      if (!containerName) {
        return;
      }

      logger.report(
        buttonClickSrc164Evid324({
          ...getBaseParams(),
          button_name: BACK_BUTTON_NAME,
          container_name: containerName,
        }),
      );
    },
    [logger],
  );

  const trackValueUpdate = useCallback(
    (
      actionName: StylesDerivedNativeActionNames,
      payload: ValueChangePayload,
    ) => {
      const panelName = PANEL_NAME_BY_ACTION[actionName];
      if (!panelName) {
        return;
      }

      logger.log({
        ...getBaseComponentChangeLogParams(),
        control_type: payload.controlType || CONTROL_TYPE_PLACEHOLDER,
        control_id: payload.controlId || CONTROL_ID_PLACEHOLDER,
        prev_value: payload.prevValues,
        new_value: payload.newValues,
        component_type: payload.targetComponents?.leadingGroupInfo
          ?.componentType
          ? payload.targetComponents.leadingGroupInfo.componentType
          : '',
        component_id: payload.targetComponents
          ? getTargetComponentsIDList(payload.targetComponents)
          : '',
        element_name: payload.targetComponents
          ? getTargetComponentsFormattedDisplayNames(payload.targetComponents)
          : '',
        panel_name: panelName,
      });
    },
    [logger],
  );

  const trackPresetApply = useCallback(
    (
      actionName: StylesDerivedNativeActionNames,
      payload: ValueChangePayload,
    ) => {
      const panelName = PANEL_NAME_BY_ACTION[actionName];
      const controlId = PRESET_CONTROL_ID_BY_ACTION[actionName];

      if (!panelName || !controlId) {
        return;
      }

      logger.log({
        ...getBaseComponentChangeLogParams(),
        control_type: PRESET_CONTROL_TYPE,
        control_id: controlId,
        prev_value: payload.prevValues,
        new_value: payload.newValues,
        component_type: payload.targetComponents?.leadingGroupInfo
          ?.componentType
          ? payload.targetComponents.leadingGroupInfo.componentType
          : '',
        component_id: payload.targetComponents
          ? getTargetComponentsIDList(payload.targetComponents)
          : '',
        element_name: payload.targetComponents
          ? getTargetComponentsFormattedDisplayNames(payload.targetComponents)
          : '',
        panel_name: panelName,
      });
    },
    [logger],
  );

  return {
    trackLobbyItemClick,
    trackBackButtonClick,
    trackPresetApply,
    trackValueUpdate,
  };
};
